import {
	clearSketchEntityReferenceTag,
	writeSketchEntityReferenceTag,
} from '../model/SketchEntityReferenceIds';
import type { Object3D } from 'three';
import {
	resolveSketchEntityReferenceBoundaryMemberState,
	type SketchEntityReferenceRecord,
} from '../model/SketchEntityReferenceRecord';
import type {
	SketchEntityReferenceHistoryDeleteInput,
	SketchEntityReferenceHistoryMutationServiceDependencies,
	SketchEntityReferenceHistoryRestoreInput,
} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceHistoryMutationService';

export type {
	SketchEntityReferenceHistoryDeleteInput,
	SketchEntityReferenceHistoryMutationRuntime,
	SketchEntityReferenceHistoryMutationServiceDependencies,
	SketchEntityReferenceHistoryRestoreInput,
} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceHistoryMutationService';

/**
 * 同步应用转换实体引用的 Undo/Redo 状态。
 *
 * 该服务把 Record 与目标对象 tag 视为一个不可拆分的历史补丁。当前补丁尚未完整返回时，
 * PatchApplier 不会替它执行 rollback，因此任何中途失败都必须先在服务内部恢复先前状态。
 */
export class SketchEntityReferenceHistoryMutationService {
	/** 当前 Viewer 的引用仓库与目标对象解析能力。 */
	private readonly dependencies: SketchEntityReferenceHistoryMutationServiceDependencies;

	/**
	 * 创建同步历史变更服务。
	 *
	 * @param dependencies 当前 Viewer 的稳定依赖。
	 */
	public constructor(dependencies: SketchEntityReferenceHistoryMutationServiceDependencies) {
		this.dependencies = dependencies;
	}

	/**
	 * 同步恢复引用记录并重新写入目标对象 tag。
	 *
	 * @param input 目标记录与应用前记录。
	 */
	public restoreReferenceSync(input: SketchEntityReferenceHistoryRestoreInput): void {
		const targetObject = this.dependencies.runtime.getSketchObject(
			input.reference.targetSketchId,
			input.reference.targetEntityId,
		);
		if (!targetObject) {
			throw new Error(
				`恢复转换实体引用历史状态失败：目标草图实体不存在，referenceId=${input.reference.referenceId}，targetEntityId=${input.reference.targetEntityId}`,
			);
		}

		try {
			this.dependencies.repository.restoreReferenceSync(input.reference);
			if (resolveSketchEntityReferenceBoundaryMemberState(input.reference) === 'detached') {
				clearSketchEntityReferenceTag(targetObject);
			} else {
				writeSketchEntityReferenceTag(targetObject, {
					referenceId: input.reference.referenceId,
					isReferencedEntity: true,
				});
			}
		} catch (error) {
			const compensationErrors = this.compensateRestoreFailure(
				input.reference,
				input.previousReference,
				targetObject,
			);
			this.throwMutationError('恢复', input.reference.referenceId, error, compensationErrors);
		}
	}

	/**
	 * 同步删除引用记录，并在目标对象仍存在时清理 tag。
	 *
	 * Undo 创建时，前序草图补丁可能已经删除目标实体；这种缺失属于合法状态。
	 *
	 * @param input 引用 ID 与删除前记录。
	 */
	public deleteReferenceSync(input: SketchEntityReferenceHistoryDeleteInput): void {
		this.dependencies.repository.deleteReferenceSync(input.referenceId);
		if (!input.previousReference) {
			return;
		}

		let targetObject: Object3D | null = null;
		try {
			targetObject = this.dependencies.runtime.getSketchObject(
				input.previousReference.targetSketchId,
				input.previousReference.targetEntityId,
			);
			if (!targetObject) {
				return;
			}
			clearSketchEntityReferenceTag(targetObject);
		} catch (error) {
			const compensationErrors: unknown[] = [];
			try {
				this.dependencies.repository.restoreReferenceSync(input.previousReference);
			} catch (compensationError) {
				compensationErrors.push(compensationError);
			}
			try {
				const compensationTarget = targetObject ?? this.dependencies.runtime.getSketchObject(
					input.previousReference.targetSketchId,
					input.previousReference.targetEntityId,
				);
				if (!compensationTarget) {
					throw new Error(`历史失败补偿无法找到先前目标实体：${input.previousReference.targetEntityId}`);
				}
				writeSketchEntityReferenceTag(compensationTarget, {
					referenceId: input.previousReference.referenceId,
					isReferencedEntity: true,
				});
			} catch (compensationError) {
				compensationErrors.push(compensationError);
			}
			this.throwMutationError('删除', input.referenceId, error, compensationErrors);
		}
	}

	/**
	 * 恢复补丁中途失败后，回到应用前的 Record 与 tag 状态。
	 *
	 * @param attemptedReference 本次尝试恢复的记录。
	 * @param previousReference 应用前记录。
	 * @param attemptedTarget 本次尝试写入的目标对象。
	 * @returns 补偿过程中额外发生的异常。
	 */
	private compensateRestoreFailure(
		attemptedReference: SketchEntityReferenceRecord,
		previousReference: SketchEntityReferenceRecord | null,
		attemptedTarget: Object3D,
	): unknown[] {
		const compensationErrors: unknown[] = [];
		try {
			if (previousReference) {
				this.dependencies.repository.restoreReferenceSync(previousReference);
			} else {
				this.dependencies.repository.deleteReferenceSync(attemptedReference.referenceId);
			}
		} catch (error) {
			compensationErrors.push(error);
		}

		try {
			clearSketchEntityReferenceTag(attemptedTarget);
		} catch (error) {
			compensationErrors.push(error);
		}

		if (previousReference) {
			const previousTarget = this.dependencies.runtime.getSketchObject(
				previousReference.targetSketchId,
				previousReference.targetEntityId,
			);
			if (!previousTarget) {
				compensationErrors.push(new Error(
					`历史失败补偿无法找到先前目标实体：${previousReference.targetEntityId}`,
				));
			} else {
				try {
					if (resolveSketchEntityReferenceBoundaryMemberState(previousReference) === 'detached') {
						clearSketchEntityReferenceTag(previousTarget);
					} else {
						writeSketchEntityReferenceTag(previousTarget, {
							referenceId: previousReference.referenceId,
							isReferencedEntity: true,
						});
					}
				} catch (error) {
					compensationErrors.push(error);
				}
			}
		}

		return compensationErrors;
	}

	/**
	 * 用统一上下文抛出历史变更错误，并保留原始异常与补偿异常。
	 *
	 * @param operation 当前操作。
	 * @param referenceId 引用记录 ID。
	 * @param error 原始失败。
	 * @param compensationErrors 补偿失败集合。
	 */
	private throwMutationError(
		operation: '恢复' | '删除',
		referenceId: string,
		error: unknown,
		compensationErrors: readonly unknown[],
	): never {
		const message = `${operation}转换实体引用历史状态失败：referenceId=${referenceId}`;
		if (compensationErrors.length > 0) {
			throw new AggregateError(
				[error, ...compensationErrors],
				`${message}，且失败补偿未完整完成`,
			);
		}
		throw new Error(message, { cause: error });
	}
}
